import {
  Formatter,
  requestBlogsSSR,
  useMobile,
} from "@with-nx/hooks-n-helpers";
import { DesignedButton, DesignedCard, Rule } from "@with-nx/simple-ui/atoms";
import { ReusableSection } from "@with-nx/simple-ui/atoms";
import { BlueUnderline, VisibleContainer } from "@with-nx/simple-ui/molecules";
import {
  CatalogLayout,
  GridBlog,
  GridStepScroller,
  ProductLandingHero,
  ReusablePricingBox,
} from "@with-nx/simple-ui/organisms";
import { Col, Row } from "antd";
import Image from "next/image";
import { useRouter } from "next/router";
import { Box } from "simple-effing-primitive-layout";

import { ChoreoPricingCard } from "../components/w/ChoreoPricingCard";
import ReusableSearchSection from "../components/w/ReusableSearchSection";

const ChoreographyGuides = ({ resolvedUrl, blogs }) => {
  const router = useRouter();
  const mobile = useMobile();

  return (
    <CatalogLayout
      footerNoAngle
      title="Broadway Media - Choreography Guides"
      description="Learn from Broadway professionals and empower your production with a toolbox of steps - the ultimate guide for your show!"
      resolvedUrl={resolvedUrl}
    >
      <ProductLandingHero
        title={
          <Rule parse="d:block" tag="h1">
            <Rule parse="!hs d:block mb:12 ta:center" tag="span">
              Elevate your production with
            </Rule>
            <Rule parse="!dm d:block mb:12 ta:center" weight="700" tag="span">
              Choreography Guides
            </Rule>
          </Rule>
        }
        description=" Learn from Broadway professionals and empower your production with a
          toolbox of steps - the ultimate guide for your show!"
        primaryButtonLabel="Find Your Show"
        primaryButtonHref="#search-choreo"
        secondaryButtonLabel="Schedule Consultation"
        secondaryButtonPress={() => {
          router.push("/contact/calendar");
        }}
      >
        <Box css="ml:auto mr:auto d:block" style={{ maxWidth: 1000 }}>
          <Image
            src="/assets/section/choreography-guides-hero-section.webp"
            alt="Choreography Guides"
            width={1000}
            height={582}
            priority
          />
        </Box>
      </ProductLandingHero>

      <ReusableSection>
        <div className="container">
          <Row gutter={[36, 18]}>
            <Col xs={24}>
              <Rule
                parse="!hl d:block mb:12"
                css="--blue-gradient-text"
                tag="h3"
              >
                It’s Time to Revolutionize the Way You Choreograph Musicals!
              </Rule>
              <Rule parse="!pl d:block mb:12 c:?font3">
                Choreography Guides are easy-to-use digital video guides, which
                include 3 - 4 hours of instruction, and time-saving tips on how
                to adapt the choreography for the unique needs of your
                production.
              </Rule>
            </Col>
          </Row>
        </div>
      </ReusableSection>

      <ReusableSection>
        <div className="container">
          <Row gutter={[0, 18]}>
            <Row gutter={[36, 18]} align={"middle"}>
              <Col xs={24} sm={24} md={12}>
                <DesignedCard
                  size="16x9"
                  embed="https://www.youtube.com/watch?v=3lFGV56ERMk"
                  muted={false}
                  loop={false}
                  controls={true}
                  playing={false}
                />
              </Col>

              <Col xs={24} sm={24} md={12}>
                <Rule parse="!ht d:block mb:12 c:?font2" tag="h4">
                  Share the Magic of Theatre
                </Rule>
                <Rule parse={`!pl d:block c:?font3 ${mobile && "mb:36"}`}>
                  Let Choreography Guides empower you to build confidence and
                  create awe-inspiring performances that will captivate
                  audiences for years to come. Share the magic of Choreography
                  Guides with your entire cast with our cloud access feature
                  that allows your cast to conveniently access videos anytime
                  and anywhere, fostering collaboration and ensuring everyone is
                  on the same page. With digital delivery upon ordering, you can
                  jumpstart your rehearsals and enjoy up to 1-year of rehearsal
                  time, giving your team ample opportunity to perfect every
                  movement.
                </Rule>
              </Col>
            </Row>

            <Row gutter={[36, 18]} align={"middle"}>
              <Col xs={24} sm={24} md={12}>
                <Rule parse="!ht d:block mb:12 c:?font2" tag="h4">
                  Learn from the Best in the Industry
                </Rule>
                <Rule parse={`!pl d:block c:?font3 ${mobile && "mb:36"}`}>
                  Developed by Tony-winning director/choreographer{" "}
                  <a
                    target="_blank"
                    href="https://m.playbill.com/person/jerry-mitchell-vault-0000000581"
                    rel="nofollow"
                  >
                    Jerry Mitchell
                  </a>
                  and Broadway veteran, and Creative Director of Broadway Media,
                  <a
                    target="_blank"
                    href="https://www.playbill.com/person/paul-canaan-vault-0000067711"
                    rel="nofollow"
                  >
                    Paul Canaan
                  </a>
                  . Together, in partnership with{" "}
                  <a rel="noreferrer" href="/partners/mti">
                    MTI
                  </a>{" "}
                  and{" "}
                  <a rel="noreferrer" href="/partners/trw">
                    TRW
                  </a>
                  , Broadway Media presents “Choreography Guides”, your access
                  to Broadway-level choreography. This online video resource
                  provides you with the tools and techniques to flawlessly
                  execute an unforgettable production!
                </Rule>
              </Col>
              <Col xs={24} sm={24} md={12}>
                <DesignedCard
                  image="/assets/section/choreography-guides-landing-page-1.webp"
                  alt="A group of five people smiling in front of a white wall with black windows."
                  size="16x9"
                />
              </Col>
            </Row>

            <Row gutter={[36, 18]} align={"middle"}>
              <Col xs={24} sm={24} md={12}>
                <DesignedCard
                  image="/assets/section/choreography-guides-landing-page-2.webp"
                  alt="A man filming three people dancing with a video camera on a tripod."
                  size="16x9"
                />
              </Col>
              <Col xs={24} sm={24} md={12}>
                <Rule parse="!ht d:block mb:12 c:?font2" tag="h4">
                  Take your Productions to the Next Level
                </Rule>
                <Rule parse={`!pl d:block c:?font3 ${mobile && "mb:36"}`}>
                  Choreography Guides make choreography a breeze, allowing you
                  to focus on creating unforgettable moments on stage. Say
                  goodbye to the guesswork and embrace the freedom to create
                  captivating performances. By breaking down choreography into
                  bite size chunks taught by top-notch industry pros, Broadway
                  choreography becomes accessible to theatre programs of all
                  skill sets and budgets.
                </Rule>
              </Col>
            </Row>

            <Row gutter={[36, 18]} align={"middle"}>
              <Col xs={24} sm={24} md={12}>
                <Rule parse="!ht d:block mb:12 c:?font2" tag="h4">
                  Inspire and Build Confidence through Choreography!
                </Rule>
                <Rule parse={`!pl d:block c:?font3 ${mobile && "mb:36"}`}>
                  Enhance the educational experience as you delve into the
                  stories behind the steps and learn from the actual creators
                  themselves, such as{" "}
                  <a
                    target="_blank"
                    href="https://m.playbill.com/person/jerry-mitchell-vault-0000000581"
                    rel="nofollow"
                  >
                    Jerry Mitchell
                  </a>
                  ,{" "}
                  <a
                    target="_blank"
                    href="https://www.michaelmooreagency.com/matt-west"
                    rel="nofollow"
                  >
                    Matt West
                  </a>{" "}
                  and{" "}
                  <a
                    target="_blank"
                    href="https://sergiotrujillo.com/bio"
                    rel="nofollow"
                  >
                    Sergio Trujillo
                  </a>
                  . When you choose Choreography Guides, you receive more than
                  just instructional videos. You also get the rights to perform
                  the choreography in your production, legal usage rights and
                  payment of royalties to the creators.
                </Rule>
              </Col>
              <Col xs={24} sm={24} md={12}>
                <DesignedCard
                  playing={false}
                  controls={true}
                  embed="https://www.youtube.com/watch?v=OJ9f0Es0OH8&t=509s"
                  size="16x9"
                />
              </Col>
            </Row>
          </Row>
        </div>
      </ReusableSection>

      <div id="search-choreo" />
      <VisibleContainer>
        <ReusableSection>
          <div className="container">
            <Box
              css="d:flex a:baseline j:flex-start p:relative oy:visible fw:wrap"
              parse="mb:24"
            >
              <Rule
                parse="!hl d:inline-block"
                display="block"
                bottom={12}
                right={12}
              >
                Learn from
              </Rule>
              <Box css="p:relative">
                <Rule
                  parse="!hl d:inline-block p:relative"
                  display="block"
                  css="--blue-gradient-text"
                  bottom={0}
                  style={{ marginBottom: 0 }}
                >
                  Broadway&apos;s Best!
                  <BlueUnderline />
                </Rule>
              </Box>
            </Box>
          </div>
          <ReusableSearchSection productCategory="choreo-guides" />
        </ReusableSection>
      </VisibleContainer>
      <ReusableSection>
        <div className="container">
          <Row gutter={[36, 18]}>
            <Col xs={24}>
              <Rule parse="!hl d:block mb:36">Choreography Guides</Rule>
            </Col>
            <Col xs={24} sm={24} md={12} flex={1}>
              <ChoreoPricingCard />
            </Col>
            <Col xs={24} sm={24} md={12}>
              <ReusablePricingBox
                description="Develop your skills as a dancer, with classes for beginners to advanced."
                label=""
                title="Broadway Workshop"
                price="Coming Soon"
                features={[
                  "Virtual Live Classes",
                  "In-Person Workshops",
                  "100s of Hours of Videos on Demand ",
                ]}
                onLearnMore={undefined}
                minimal={true}
                span={mobile ? 24 : 12}
                mode="top-left-grey"
              >
                <Box css="d:flex a:center j:center" parse="pt:36">
                  <DesignedButton
                    label="Request Information"
                    theme="white"
                    size="large"
                    href={`/contact/start?mode=learn-more&referrer=${router.asPath}`}
                  />
                </Box>
              </ReusablePricingBox>
            </Col>
          </Row>
        </div>
      </ReusableSection>
      {blogs?.length > 0 ? (
        <ReusableSection background="transparent">
          <div className="container">
            <Rule parse="!hl d:block mb:36">Choreography Guides Stories</Rule>
            <GridStepScroller
              xs={24}
              sm={24}
              md={12}
              lg={8}
              xl={8}
              items={blogs.blogs.map((blog, b) => {
                if (b > 2) {
                  return null;
                }

                return (
                  <GridBlog
                    key={blog._id}
                    date={blog.date}
                    description={blog.description}
                    image={Formatter.compressed(blog.coverImage, 256, true)}
                    title={blog.title}
                    url={`/blog/${blog.slug}`}
                    transparent={false}
                    properties={{
                      height: "100%",
                    }}
                  />
                );
              })}
            />
            <Box css="d:flex j:flex-end" parse="mt:24">
              <DesignedButton
                size="small"
                theme="tetriary"
                label="See More Stories"
                href="/blog"
              />
            </Box>
          </div>
        </ReusableSection>
      ) : undefined}
    </CatalogLayout>
  );
};

export default ChoreographyGuides;

export const getServerSideProps = async (context) => {
  const { resolvedUrl } = context;

  const blogs =
    (await requestBlogsSSR({
      exists: {
        choreoGuideTags: true,
      },
    })) || [];

  return {
    props: {
      resolvedUrl,
      blogs,
    },
  };
};
